import React, { useCallback, useEffect, useState } from 'react'

import {
  Body2,
  Body3,
  BorderStyle,
  Box,
  Checkbox,
  Color,
  Join,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { useDispatch, useStore } from 'react-redux'

import { actionTypes } from 'actions/actionTypes'

import { RecipeCustomisationPaintedDoorDialogue } from './RecipeCustomisationPaintedDoorDialogue'
import { RecipeCustomisationTitle } from './RecipeCustomisationTitle'
import { CustomisationOption } from './getCustomisationForRecipe'

import css from './RecipeCustomisation.css'

const RecipeCustomisationBox = withCitrusTheme(Box, () => ({
  borderRadius: '4px',
}))

export type IngredientCustomisationSectionProps = {
  checked: boolean
  customisation: CustomisationOption
  firstName?: string
  onClick: () => void
  checkboxClicked: boolean
  setCheckboxClicked: (value: boolean) => void
  setOrCancelTimeout: () => void
  shouldShowConfirmationDialogue: boolean
  recipeId: string
}

const RecipeCustomisationSection: React.FC<IngredientCustomisationSectionProps> = ({
  checked,
  customisation,
  firstName,
  onClick,
  checkboxClicked,
  setCheckboxClicked,
  setOrCancelTimeout,
  shouldShowConfirmationDialogue,
  recipeId,
}) => {
  const store = useStore()
  const dispatch = useDispatch()
  const { basket } = store.getState()
  const [trackingEventFired, setTrackingEventFired] = useState<boolean>(false)

  const dispatchTrackingEvent = useCallback(() => {
    dispatch({
      type: actionTypes.TRACKING,
      trackingData: {
        actionType: 'screen_change',
        screen_name: 'recipe_customisation_screen',
        menuId: basket.get('currentMenuId'),
        orderId: basket.get('orderId'),
        proteinOption: customisation?.trackingSlug,
        recipeId,
      },
    })
  }, [basket, customisation, dispatch, recipeId])

  useEffect(() => {
    if (!trackingEventFired) {
      dispatchTrackingEvent()
      setTrackingEventFired(true)
    }
  }, [dispatchTrackingEvent, trackingEventFired])

  return (
    <RecipeCustomisationBox
      className={css.recipeCustomisationBox}
      borderColor={Color.NeutralGrey_200}
      borderStyle={BorderStyle.Solid}
      borderWidth={0.5}
      paddingH={2}
      paddingV={2}
      display="block"
      data-testid="customisationSection"
    >
      {shouldShowConfirmationDialogue ? (
        <RecipeCustomisationPaintedDoorDialogue firstName={firstName} />
      ) : (
        <Join with={<Space size={4} direction="vertical" />}>
          <Join with={<Space size={2} direction="vertical" />}>
            <RecipeCustomisationTitle />

            <Body3>{customisation.description}</Body3>
          </Join>

          <Checkbox
            id="customisationCheckBox"
            checked={checked}
            onChange={() => {
              setCheckboxClicked(!checkboxClicked)
              setOrCancelTimeout()
              onClick()
            }}
            paddingH={1}
            data-testid="customisationCheckBox"
          >
            <Body2 color={Color.ColdGrey_800}>{customisation.option}</Body2>
            <Body3>£1.25 / portion</Body3>
          </Checkbox>
        </Join>
      )}
    </RecipeCustomisationBox>
  )
}

export { RecipeCustomisationSection }
